/* Atlas v3 — WatchDeck: the swipe-to-build-a-watchlist deck.
   Every card is one Atlas name carrying the data engine's editorial: the
   plain-English one-liner, last night's Claude summary, thesis direction,
   and the 5D spark. Swipe right (or ★) = star it, left (or ✕) = skip,
   tap = open the full TickerSheet. The deck deals from the ACTIVE universe
   filtered by the REAL sector taxonomy (chips) — names already on the
   watchlist are never dealt.

   Used by My Stocks: inline on the empty state, and inside the
   MORE COMPANIES → sheet once the list is non-empty. Mobile-first. */
import { useMemo, useRef, useState } from 'react'
import { Spark, REDUCE } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { shortSector } from './Markets'
import { CHOKEPOINT_W } from './chokepoints'
import { ANALYSIS_PAUSED } from '@/v2/lib/analysisPaused'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

const THESIS = {
  BULLISH: { label: 'Bullish', tok: '--studio-success' },
  BEARISH: { label: 'Bearish', tok: '--studio-danger' },
  NEUTRAL: { label: 'Neutral', tok: '--studio-text-subtle' },
}

const COMMIT_PX = 88 // drag distance that commits a decision

function shuffle(arr) {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/* ── the dealer — banded order, shuffled within bands ─────────────────────
   The deck is never truncated (all names stay reachable), but the ORDER does
   the retention work — nobody's first card is a −50%-on-the-year wreck.
   None of this is user-visible copy; the UI just deals cards.

   A  openers   big durable chokepoints — tracker names (chokepoints.js) with
                mcap ≥ $15B, thesis not bearish, 1Y better than −10%.
                The GEV/VRT class; the generic hyperscalers aren't in the
                tracker, so they can't open.
   B  edge      the rest of the chokepoint list (CORE tier bumped first —
                the monopoly/oligopoly owners), demotions excluded
   C  field     everything else in the universe, demotions excluded
   D  tail      1Y < −35% — demoted deep, not hidden: honest but last

   perf1y(t) may be null (no nightly row) → dealable but never opens
   (unknown ≠ vetted). */
function deal(pool, perf1y) {
  const a = [], b = [], c = [], d = []
  for (const s of pool) {
    const w = CHOKEPOINT_W[s.ticker] || 0
    const y = perf1y(s.ticker)
    const demoted = y != null && y < -35
    const mcap = Number(s.market_cap_usd_m) || 0
    const bearish = (s.thesis_status || '').toUpperCase() === 'BEARISH'
    if (demoted) d.push(s)
    else if (w > 0 && mcap >= 15000 && !bearish && y != null && y > -10) a.push(s)
    else if (w > 0) b.push({ s, w })
    else c.push(s)
  }
  const edge = [3, 2, 1].flatMap((w) => shuffle(b.filter((x) => x.w === w)).map((x) => x.s))
  return [...shuffle(a), ...edge, ...shuffle(c), ...shuffle(d)]
}

function DeckCard({ s, spark, depth, drag, onOpen }) {
  const th = THESIS[(s.thesis_status || '').toUpperCase()] ?? null
  const oneLiner = s.one_liner || s.what_they_do || null
  const dx = depth === 0 ? drag : 0
  const settled = depth === 0 && drag === 0
  const up = spark && spark.length > 1 ? spark[spark.length - 1] >= spark[0] : true
  return (
    <div style={{
      position: 'absolute', inset: 0,
      transform: depth === 0
        ? `translateX(${dx}px) rotate(${dx / 14}deg)`
        : `translateY(${depth * 9}px) scale(${1 - depth * 0.045})`,
      transition: settled || depth > 0 ? 'transform 260ms var(--studio-ease)' : 'none',
      background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))',
      borderRadius: 'var(--studio-radius-lg)', padding: '16px 17px',
      display: 'flex', flexDirection: 'column', zIndex: 3 - depth,
      boxShadow: depth === 0 ? '0 14px 40px hsl(var(--studio-overlay) / 0.55)' : 'none',
      touchAction: 'pan-y',
    }}
      onClick={depth === 0 ? onOpen : undefined}>
      {/* drag verdict hints — light up with pull direction */}
      <div style={{ position: 'absolute', top: 14, right: 16, ...Mono, fontSize: 10, letterSpacing: '0.14em',
        color: 'hsl(var(--studio-accent))', opacity: Math.min(1, Math.max(0, dx) / 110), pointerEvents: 'none' }}>★ WATCH</div>
      <div style={{ position: 'absolute', top: 14, left: 16, ...Mono, fontSize: 10, letterSpacing: '0.14em',
        color: 'hsl(var(--studio-text-subtle))', opacity: Math.min(1, Math.max(0, -dx) / 110), pointerEvents: 'none' }}>SKIP ✕</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <StockLogo ticker={s.ticker} size={34} rounded="50%" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...Mono, fontSize: 15, color: 'hsl(var(--studio-text))' }}>{s.ticker}</div>
          <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
        </div>
        {th && (
          <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 7px',
            borderRadius: 'var(--studio-radius-sm)', color: `hsl(var(${th.tok}))`, border: `1px solid hsl(var(${th.tok}) / 0.4)`, flexShrink: 0 }}>
            {th.label}
          </span>
        )}
      </div>

      {oneLiner && (
        <div style={{ ...Display, fontSize: 15.5, lineHeight: 1.38, color: 'hsl(var(--studio-text))', marginTop: 13,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {oneLiner}
        </div>
      )}
      {!ANALYSIS_PAUSED && s.cron_summary && (
        <div style={{ ...Sans, fontSize: 12, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', marginTop: 9,
          display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {s.cron_summary}
        </div>
      )}

      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 12, borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
        <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }}>
          {shortSector(s.sector) || 'Atlas'} · TAP FOR THE FULL SHEET
        </span>
        <Spark data={spark} up={up} width={56} height={20} />
      </div>
    </div>
  )
}

export function WatchDeck({ stocks, sparks, watched, onStar, onOpen, perf1y = () => null }) {
  const [sector, setSector] = useState('all')
  const [seed, setSeed] = useState(0) // bump to reshuffle after a cleared deck
  const [idx, setIdx] = useState(0)
  const [drag, setDrag] = useState(0)
  const [flying, setFlying] = useState(0) // ±1 while a card animates off
  const ptr = useRef(null) // { x, moved }

  const sectors = useMemo(() => [...new Set((stocks || []).map((s) => s.sector).filter(Boolean))].sort(), [stocks])

  // Deal order — banded by the dealer per sector/seed. `watched` is filtered
  // at DEAL time (below), not here, so starring never reshuffles the deck.
  const deck = useMemo(() => {
    const pool = (stocks || []).filter((s) =>
      (sector === 'all' || s.sector === sector) && (s.one_liner || s.what_they_do || (!ANALYSIS_PAUSED && s.cron_summary)))
    return deal(pool, perf1y)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks, sector, seed])

  const remaining = useMemo(() => deck.slice(idx).filter((s) => !watched.has(s.ticker)), [deck, idx, watched])
  const visible = remaining.slice(0, 3)
  const top = visible[0]

  const pickSector = (v) => { setSector(v); setIdx(0); setDrag(0); setFlying(0) }

  function commit(dir) {
    if (!top || flying) return
    if (REDUCE) { // no fling animation — decide and advance instantly
      if (dir > 0) onStar(top.ticker)
      setIdx((i) => i + 1); setDrag(0)
      return
    }
    setFlying(dir)
    setDrag(dir * (window.innerWidth || 420))
    setTimeout(() => {
      if (dir > 0) onStar(top.ticker)
      setIdx((i) => i + 1); setDrag(0); setFlying(0)
    }, 240)
  }

  const onPtrDown = (e) => { if (flying) return; ptr.current = { x: (e.touches?.[0] ?? e).clientX, moved: false } }
  const onPtrMove = (e) => {
    if (!ptr.current || flying) return
    const dx = (e.touches?.[0] ?? e).clientX - ptr.current.x
    if (Math.abs(dx) > 6) ptr.current.moved = true
    setDrag(dx)
  }
  const suppressClick = useRef(false)
  const onPtrUp = () => {
    if (!ptr.current || flying) return
    const moved = ptr.current.moved
    ptr.current = null
    if (Math.abs(drag) > COMMIT_PX) commit(drag > 0 ? 1 : -1)
    else setDrag(0)
    if (moved) suppressClick.current = true
  }
  const openTop = () => {
    if (suppressClick.current) { suppressClick.current = false; return }
    if (top) onOpen(top.ticker)
  }

  return (
    <div>
      {/* sector chips — the REAL universe taxonomy, same short names as Markets */}
      <div className="atlas-scroll" style={{ display: 'flex', gap: 5, overflowX: 'auto', padding: '0 20px 12px' }}>
        {[['all', 'All'], ...sectors.map((s) => [s, shortSector(s)])].map(([v, label]) => {
          const on = sector === v
          return (
            <button key={v} onClick={() => pickSector(v)} style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '5px 11px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              background: on ? 'hsl(var(--studio-surface-2))' : 'transparent',
              color: on ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))',
              border: `1px solid ${on ? 'hsl(var(--studio-accent) / 0.4)' : 'hsl(var(--studio-border) / 0.5)'}` }}>{label}</button>
          )
        })}
      </div>

      {/* the deck */}
      <div style={{ position: 'relative', height: 264, margin: '0 20px' }}
        onPointerDown={onPtrDown} onPointerMove={onPtrMove} onPointerUp={onPtrUp} onPointerCancel={onPtrUp} onPointerLeave={onPtrUp}>
        {top ? (
          [...visible].reverse().map((s, i) => {
            const depth = visible.length - 1 - i
            return <DeckCard key={s.ticker} s={s} spark={sparks?.[s.ticker] || null} depth={depth} drag={drag} onOpen={openTop} />
          })
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
            border: '1px dashed hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)' }}>
            <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))' }}>DECK CLEARED — EVERY NAME TRIAGED</span>
            <button onClick={() => { setSeed((n) => n + 1); setIdx(0) }} style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', padding: '6px 14px', borderRadius: 999,
              cursor: 'pointer', background: 'transparent', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.4)' }}>DEAL AGAIN</button>
          </div>
        )}
      </div>

      {/* tap fallbacks (also the a11y path) */}
      {top && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 46, marginTop: 16 }}>
          <button onClick={() => commit(-1)} aria-label={`Skip ${top.ticker}`} style={{ width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
            background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 16 }}>✕</button>
          <button onClick={() => commit(1)} aria-label={`Watch ${top.ticker}`} style={{ width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
            background: 'hsl(var(--studio-accent) / 0.12)', border: '1px solid hsl(var(--studio-accent) / 0.55)', color: 'hsl(var(--studio-accent))', fontSize: 17 }}>★</button>
        </div>
      )}
      {top && (
        <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', textAlign: 'center', marginTop: 10 }}>
          {remaining.length} MORE IN THE DECK
        </div>
      )}
    </div>
  )
}
